import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { verifyResetToken, resetPassword } from '../api/authApi';

type PageState = 'verifying' | 'invalid' | 'form' | 'submitting' | 'success' | 'error';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pageState, setPageState] = useState<PageState>('verifying');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenEmail, setTokenEmail] = useState<string | undefined>();

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setErrorMessage('No reset token provided');
        setPageState('invalid');
        return;
      }

      try {
        const result = await verifyResetToken(token);
        if (result.valid) {
          setTokenEmail(result.email);
          setPageState('form');
        } else {
          setErrorMessage('This password reset link is invalid or has expired');
          setPageState('invalid');
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Invalid or expired reset link');
        setPageState('invalid');
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setErrorMessage(passwordError);
      setPageState('error');
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setPageState('error');
      return;
    }

    setPageState('submitting');

    try {
      await resetPassword({ token, newPassword: password });
      setPageState('success');

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to reset password');
      setPageState('error');
    }
  };

  // Loading state while verifying token
  if (pageState === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 py-12 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-slate-400">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 py-12 px-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-error-100 dark:bg-error-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-error-600 dark:text-error-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Invalid Reset Link
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">{errorMessage}</p>
          <div className="space-y-3">
            <Link
              to="/forgot-password"
              className="block w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 transition-colors text-center"
            >
              Request New Reset Link
            </Link>
            <Link
              to="/login"
              className="block w-full px-6 py-3 border border-primary-200 dark:border-primary-800 text-gray-700 dark:text-slate-300 font-medium rounded-xl bg-white/50 dark:bg-slate-900/50 hover:bg-white/70 dark:hover:bg-slate-800/70 hover:border-primary-300 dark:hover:border-primary-700 transition-colors text-center"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 py-12 px-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-success-600 dark:text-success-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Password Reset Successful
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            Your password has been reset successfully. You will be redirected to the login page
            shortly.
          </p>
          <Link
            to="/login"
            className="inline-block w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 transition-colors text-center"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Password reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 py-12 px-4">
      <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Reset Password
          </h1>
          {tokenEmail && (
            <p className="text-gray-600 dark:text-slate-400">
              Enter a new password for{' '}
              <strong className="text-primary-700 dark:text-primary-400">{tokenEmail}</strong>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
            >
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (pageState === 'error') {
                  setPageState('form');
                  setErrorMessage('');
                }
              }}
              placeholder="Enter new password"
              className="w-full px-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-primary-200 dark:border-primary-800 rounded-xl placeholder-gray-400 dark:placeholder-slate-500 text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              disabled={pageState === 'submitting'}
              autoComplete="new-password"
              autoFocus
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (pageState === 'error') {
                  setPageState('form');
                  setErrorMessage('');
                }
              }}
              placeholder="Confirm new password"
              className="w-full px-4 py-3 bg-white/50 dark:bg-slate-900/50 border border-primary-200 dark:border-primary-800 rounded-xl placeholder-gray-400 dark:placeholder-slate-500 text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              disabled={pageState === 'submitting'}
              autoComplete="new-password"
            />
          </div>

          <div className="p-4 bg-primary-50/50 dark:bg-primary-900/20 backdrop-blur-sm border border-primary-200/50 dark:border-primary-800/50 rounded-xl">
            <p className="text-xs text-gray-700 dark:text-slate-300 font-medium mb-2">
              Password Requirements:
            </p>
            <ul className="text-xs space-y-1">
              <li
                className={
                  password.length >= 8
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-gray-500 dark:text-slate-400'
                }
              >
                - At least 8 characters
              </li>
              <li
                className={
                  /[A-Z]/.test(password)
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-gray-500 dark:text-slate-400'
                }
              >
                - One uppercase letter
              </li>
              <li
                className={
                  /[a-z]/.test(password)
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-gray-500 dark:text-slate-400'
                }
              >
                - One lowercase letter
              </li>
              <li
                className={
                  /[0-9]/.test(password)
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-gray-500 dark:text-slate-400'
                }
              >
                - One number
              </li>
              <li
                className={
                  /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-gray-500 dark:text-slate-400'
                }
              >
                - One special character (!@#$%^&* etc.)
              </li>
            </ul>
          </div>

          {pageState === 'error' && errorMessage && (
            <div className="p-4 bg-error-50/80 dark:bg-error-900/30 backdrop-blur-sm border border-error-200/50 dark:border-error-800/50 rounded-xl">
              <p className="text-sm text-error-600 dark:text-error-400">{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={pageState === 'submitting'}
            className="w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {pageState === 'submitting' ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
